import React, { ReactElement, useState, useEffect } from 'react'
import { Navigate, matchPath } from 'react-router-dom'
import { getCurrentUser } from 'helpers/auth'
import { useAuth } from 'hooks/auth'
import { PROFILE, SIGN_IN, DOCUMENT, TABLE, SETTINGS, HUB, PROJECT, PROCESS, OLD_TABLE } from 'routes'
import { saveCurrentUrlForRedirect } from 'helpers/redirect'

interface AuthGuardProps {
  children: ReactElement
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [redirect, setRedirect] = useState(false)
  const cookieData = getCurrentUser()
  const { user, updateUser } = useAuth()
  // Track whether the user had a session on first mount so we can distinguish
  // "session expired" from "never logged in" (initializer runs once)
  const [hadSession] = useState(() => !!cookieData)

  // React Router v6: matchPath(pattern, pathname)
  const allowedOptionalAuth =
    !!matchPath(SIGN_IN, window.location.pathname) ||
    !!matchPath(DOCUMENT, window.location.pathname) ||
    !!matchPath(TABLE, window.location.pathname) ||
    !!matchPath(SETTINGS, window.location.pathname) ||
    !!matchPath(PROFILE, window.location.pathname) ||
    !!matchPath(HUB, window.location.pathname) ||
    !!matchPath(PROJECT, window.location.pathname) ||
    !!matchPath(PROCESS, window.location.pathname) ||
    !!matchPath(OLD_TABLE, window.location.pathname)

  const tryLoadingUserFromLocalStorage = async () => {
    // Use details from localstage, so any api calls will use those creds
    await updateUser(cookieData!)
  }

  useEffect(() => {
    // load user info from local storage
    if (cookieData && !user) {
      tryLoadingUserFromLocalStorage()
    }

    // redirect to login page
    if (!user && !cookieData) {
      setRedirect(true)
    }
  }, [cookieData, user])
  if (redirect && !allowedOptionalAuth) {
    // Save the current URL so the user can return after signing in
    saveCurrentUrlForRedirect()
    const reason = hadSession ? '?reason=sessionExpired' : ''
    return <Navigate to={`${SIGN_IN}${reason}`} replace />
  }

  if (!user && !allowedOptionalAuth) {
    return null
  }
  return children
}

export default AuthGuard
